import React from "react";
import PropTypes from "prop-types";
import "./tag.scss";

/**
 * Tag Component - Reusable badge/tag component
 * Used throughout the application for status indicators, labels, and categories
 *
 * @param {string} color - Color variant: 'primary', 'secondary', 'success', 'danger', 'warning', 'info', 'dark', 'light', 'purple', 'pink', 'cyan', 'orange'
 * @param {string} content - Text content to display in the tag
 * @param {boolean} showDot - Whether to show a dot indicator before the text (default: false)
 * @param {string} size - Size variant: 'sm', 'md', 'lg' (default: 'md')
 * @param {string} className - Additional CSS classes
 * @param {function} onClick - Optional click handler
 */
const Tag = ({
  color = "primary",
  content,
  showDot = false,
  size = "md",
  className = "",
  onClick
}) => {
  const baseClass = "custom-tag";
  const colorClass = `custom-tag-${color}`;
  const sizeClass = `custom-tag-${size}`;
  const clickableClass = onClick ? "custom-tag-clickable" : "";

  const combinedClassName = `${baseClass} ${colorClass} ${sizeClass} ${clickableClass} ${className}`.trim();

  return (
    <span
      className={combinedClassName}
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyPress={onClick ? (e) => e.key === 'Enter' && onClick(e) : undefined}
    >
      {showDot && <span className="custom-tag-dot"></span>}
      {content}
    </span>
  );
};

Tag.propTypes = {
  color: PropTypes.oneOf([
    'primary',
    'secondary',
    'success',
    'danger',
    'warning',
    'info',
    'dark',
    'light',
    'purple',
    'pink',
    'cyan',
    'orange',
    'teal',
    'indigo'
  ]),
  content: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number
  ]).isRequired,
  showDot: PropTypes.bool,
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  className: PropTypes.string,
  onClick: PropTypes.func
};

export default Tag;
